import React from 'react';
import { useDispatch } from 'react-redux';

import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Grid from '@mui/material/Grid';

import { styled, alpha, lighten } from '@mui/material/styles';

import { setSelectedFlow } from '../../../../../actions/casesActions';
import { procedureFlows, selectedProcedureFlows, matorthoProcedureFlows } from '../../../../../constants/cases';

import osteotomy_image from '../../../../../assets/step0/PS_Osteotomy_Image.png';
import acl_image from '../../../../../assets/step0/PS_ACL_Image.png';
import cmf_image from '../../../../../assets/step0/PS_CMF_Extended_Image.png';
import oncol_image from '../../../../../assets/step0/PS_ONCOL_Image.png';
import other_image from '../../../../../assets/step0/PS_Other_Image.png';
import vilex_image from '../../../../../assets/step0/PS_Vilex_Image.png';
import matortho_image from '../../../../../assets/step0/PS_Matortho_Image.png';

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  backgroundColor: theme.palette.background.paper,

  '& .block-header': {
    width: '90%',
    marginTop: 10,
    marginBottom: 25,
  },
}));

const StyledListItem = styled(ListItem)(({ selected, backgroundColor, theme }) => ({
  borderRadius: 20,
  cursor: 'pointer',
  border: `2px solid ${backgroundColor}`,
  color: theme.palette.text.primary,
  backgroundColor: selected
    ? `${theme.palette.mode === 'dark'
      ? alpha(backgroundColor, 0.32)
      : lighten(backgroundColor, 0.95)} !important`
    : 'var(--color-surface)',
  '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
    color: selected
      ? (theme.palette.mode === 'dark' ? theme.palette.text.primary : undefined)
      : undefined,
  },
  '& img': {
    filter: selected && theme.palette.mode !== 'dark' ? 'brightness(1.05) contrast(1.05)' : 'none',
  },
}));

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  '& .MuiListItemText-primary': {
    fontSize: '1.2rem',
    fontWeight: 600,
    letterSpacing: 1.5,
    color: theme.palette.text.primary,
  },
  '& .MuiListItemText-secondary': {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
  },
}));

export const Step0Procedure = ({ selected, onChange, procedures = [] }) => {
  const dispatch = useDispatch();
  const handleProcedureFlowClick = (event, flow) => {
    onChange(flow);
    dispatch(setSelectedFlow(flow));
  };

  return (
    <Root>
      <div className="block-header">Select Procedure</div>

      <Grid container aria-label="procedures" spacing={3} sx={{ width: '95%' }}>
        {procedures.some(
          (procedure) =>
            procedure.flow === procedureFlows.HTO ||
            procedure.flow === procedureFlows.DFO_Lateral ||
            procedure.flow === procedureFlows.DFO_Medial ||
            procedure.flow === procedureFlows.HTO_LATERAL ||
            procedure.flow === procedureFlows.DOUBLE_LEVEL_OSTEOTOMY
        ) && (
          <Grid size={{ xs: 12, md: 6 }}>
            <StyledListItem
              backgroundColor="#ee9321"
              button
              selected={selected === selectedProcedureFlows.Osteotomy}
              onClick={(event) =>
                handleProcedureFlowClick(event, selectedProcedureFlows.Osteotomy)
              }
            >
              <img src={osteotomy_image} alt="Osteotomy" width="80" height="80" />
              <StyledListItemText
                primary="OSTEOTOMY"
                secondary="Osteotomies around the knee"
              />
            </StyledListItem>
          </Grid>
        )}

        {procedures.some(
          (procedure) =>
            procedure.flow === procedureFlows.CUSTOM_EXTENDED ||
            procedure.flow === procedureFlows.CUSTOM
        ) && (
          <Grid item size={{ xs: 12, md: 6 }}>
            <StyledListItem
              backgroundColor="#217cee"
              button
              selected={selected === selectedProcedureFlows.CUSTOM_EXTENDED}
              onClick={(event) =>
                handleProcedureFlowClick(event, selectedProcedureFlows.CUSTOM_EXTENDED)
              }
            >
              <img src={other_image} alt="OTHER" width="70" height="70" style={{ marginRight: '16px'}} />
              <StyledListItemText
                primary="OTHER"
                secondary="Other Orthopaedic Procedures"
              />
            </StyledListItem>
          </Grid>
        )}

        {procedures.some((procedure) => matorthoProcedureFlows.includes(procedure.flow)) && (
          <Grid item size={{ xs: 12, md: 6 }}>
            <StyledListItem
              backgroundColor="#AA2415"
              button
              selected={selected === selectedProcedureFlows.MATORTHO}
              onClick={(event) =>
                handleProcedureFlowClick(event, selectedProcedureFlows.MATORTHO)
              }
            >
              <img src={matortho_image} alt="MATORTHO" width="70" height="70" style={{ marginRight: '16px'}} />
              <StyledListItemText
                primary="MATORTHO"
                secondary="Matortho Procedures"
              />
            </StyledListItem>
          </Grid>
        )}

        {procedures.some((procedure) => procedure.flow === procedureFlows.ACL) && (
          <Grid item size={{ xs: 12, md: 6 }}>
            <StyledListItem
              backgroundColor="#e11157"
              button
              selected={selected === selectedProcedureFlows.ACL}
              onClick={(event) =>
                handleProcedureFlowClick(event, selectedProcedureFlows.ACL)
              }
            >
              <img src={acl_image} alt="ACL" width="80" height="80" />
              <StyledListItemText
                primary="ACL"
                secondary="Anterior Cruciate Ligament"
              />
            </StyledListItem>
          </Grid>
        )}

        {procedures.some((procedure) =>
          [procedureFlows.CMF, procedureFlows.CMF_EXTENDED, procedureFlows.ONCOL].includes(
            procedure.flow
          )
        ) && (
          <Grid item size={{ xs: 12, md: 6 }}>
            <StyledListItem
              backgroundColor="#33CC99"
              button
              selected={selected === selectedProcedureFlows.CMF_EXTENDED}
              onClick={(event) =>
                handleProcedureFlowClick(event, selectedProcedureFlows.CMF_EXTENDED)
              }
            >
              <img src={cmf_image} alt="CMF" width="80" height="80" />
              <StyledListItemText
                primary="CMF"
                secondary="Craniomaxillofacial"
              />
            </StyledListItem>
          </Grid>
        )}

        {procedures.some((procedure) => procedure.flow === procedureFlows.ONCOL) && (
          <Grid item  size={{ xs: 12, md: 6 }}>
            <StyledListItem
              backgroundColor="#7266FA"
              button
              selected={selected === selectedProcedureFlows.ONCOL}
              onClick={(event) =>
                handleProcedureFlowClick(event, selectedProcedureFlows.ONCOL)
              }
            >
              <img src={oncol_image} alt="ONCOL" width="80" height="80" />
              <StyledListItemText
                primary="ONCOL"
                secondary="Tumor Removal & Oncologic Reconstruction"
              />
            </StyledListItem>
          </Grid>
        )}

        {procedures.some((procedure) =>
          [procedureFlows.VILEX, procedureFlows.VILEX_H2, procedureFlows.VILEX_H3].includes(
            procedure.flow
          )
        ) && (
          <Grid item size={{ xs: 12, md: 6 }}>
            <StyledListItem
              backgroundColor="#BD1435"
              button
              selected={selected === selectedProcedureFlows.VILEX}
              onClick={(event) =>
                handleProcedureFlowClick(event, selectedProcedureFlows.VILEX)
              }
            >
              <img src={vilex_image} alt="VILEX" width="80" height="80" style={{ marginRight: '8px' }} />
              <StyledListItemText
                primary="VILEX"
                secondary="Total Ankle Replacement"
              />
            </StyledListItem>
          </Grid>
        )}
      </Grid>
    </Root>
  );
};
